import { Controller } from "@hotwired/stimulus"

// Formats a phone input as (xxx) xxx-xxxx while typing, and submits the clean
// digits via a hidden mirror input so the server stores an unformatted number.
//
// Usage:
//   <input type="tel" data-controller="phone-format" data-phone-format-name-value="sales_opportunity[phone_number]">
//
// The visible <input> has its name removed (not submitted); a hidden <input>
// with the real name carries the stripped digits.
export default class extends Controller {
  static values = { name: String }

  connect() {
    // A Turbo cache restore replays connect() against a snapshot that already
    // contains the previous mirror — drop any stale copy first, or two inputs
    // share the name and the stale (last) one wins at submit.
    this.element.parentElement
      ?.querySelectorAll(`input[type="hidden"][name="${CSS.escape(this.nameValue)}"]`)
      ?.forEach((el) => el.remove())

    this.hidden = document.createElement("input")
    this.hidden.type = "hidden"
    this.hidden.name = this.nameValue
    this.hidden.value = this.digits(this.element.value)
    this.element.after(this.hidden)
    this.element.removeAttribute("name")
    this.element.value = this.format(this.element.value)
    this.element.addEventListener("input", this.onInput)
  }

  disconnect() {
    this.element.removeEventListener("input", this.onInput)
    this.hidden?.remove()
  }

  onInput = (e) => {
    const raw = this.digits(e.target.value)
    e.target.value = this.format(raw)
    // Keep the caret at the end — simplest stable behavior for a short field.
    const end = e.target.value.length
    e.target.setSelectionRange(end, end)
    this.hidden.value = raw
  }

  digits(v) {
    let d = (v ?? "").toString().replace(/\D/g, "")
    if (d.length === 11 && d.startsWith("1")) d = d.slice(1)
    return d
  }

  format(v) {
    const d = this.digits(v)
    if (d.length === 0) return ""
    if (d.length > 10) return d
    if (d.length < 4) return `(${d}`
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
  }
}
